// React
import React, { useState, useEffect, useMemo } from 'react';

import { Box } from '@mui/material';

// Custom components
import Header from 'components/TaskAdvanceFilters/QuickFilterBuilder/Header';
import FilterRow from './FilterRow';

// Icons
import { AddRounded } from '@mui/icons-material';

// Styled
import {
  Wrapper,
  AddFilterButton,
  BottomRowWrapper,
  QuickFilterCnt,
  FilterTextRenderer,
} from './styled';

// Utils
import { cloneDeep, isEqual } from 'lodash';
import useDropdownData from 'utils/CustomHooks/useDropdownData';
import FiltersList from './FiltersList/FiltersList';
import CustomButton from 'components/Button/CustomButton';
import AddIcon from 'components/Icons/addIcon';
import EmptyFilters from './EmptyFilters/EmptyFilters';

export type FilterType = {
  filterName:
    | 'status'
    | 'startDate'
    | 'dueDate'
    | 'template'
    | 'templateSource'
    | 'assignee'
    | 'category'
    | 'location'
    | 'asset'
    | 'completedAt'
    | 'archived'
    | 'taskTypes'
    | 'isOverDue'
    | 'taskTemplates'
    | 'projects'
    | 'schedules'
    | 'priority'
    | 'createdBy'
    | '';
  comparator: 'is' | 'isNot' | '';
  value: any[];
  conditional: 'and' | 'or' | '';
};

const filterTypes = [
  'header',
  'signature',
  'takePhoto',
  'photoAnnotate',
  'instruction',
  'geoStamp',
  'dateTime',
];

interface AdvancedFilterBuilderPropTypes {
  renderCloseIcon?: boolean;
  hideFilters?: string;
  filters: any[];
  handleClose?: () => void;
  handleSetFiltersCb: (
    filters: FilterType[],
    showBasic?: boolean,
    showloader?: boolean,
  ) => void;
  handleClearFilters?: () => void | undefined;
  dropdownWidth?: DropdownWidth | null;
  templateItems?: any[];
  selectedFilters?: any;
  additonalFilters?: any;
  showResponseFilter?: boolean;
  addAnotherLabel?: string;
  hideHeader?: boolean;
  hideWhere?: boolean;
  readOnly?: boolean;
  handleSetItemFiltersCb?: (
    filters: ItemFilterType[],
    empty?: boolean,
    key?: string,
  ) => void;
}

interface DropdownWidth {
  mainDropdownWidth?: string | null;
  isDropdownWidth?: string | null;
}

const defaultFilterState: FilterType[] = [
  {
    filterName: '',
    comparator: 'is',
    value: [],
    conditional: 'and',
  },
];
export type ItemFilterType = {
  item?: string;
  comparator: string;
  value: any[];
  filterName?: string;
};

const defaultItemFilterState: ItemFilterType[] = [
  {
    item: '',
    comparator: '',
    value: [],
  },
];

export const TextRenderer = (selected, type, placeholderText = 'All') => {
  const selectedText =
    selected?.length === 0
      ? placeholderText
      : selected?.length === 1
      ? selected[0]?.label || selected[0]?.name
      : `${
          (selected[0]?.label || selected[0]?.name)?.length > 10
            ? (selected[0]?.label || selected[0]?.name)?.substring(0, 10) +
              '...'
            : (selected[0]?.label || selected[0]?.name) + '...'
        }, +${selected.length - 1}`;
  return (
    <FilterTextRenderer>
      <span>{type}:</span> {selectedText}
    </FilterTextRenderer>
  );
};

const QuickFilterBuilder = ({
  renderCloseIcon = true,
  filters,
  handleClose,
  handleSetFiltersCb,
  hideFilters = '',
  dropdownWidth = null,
  handleClearFilters,
  hideHeader = false,
  addAnotherLabel = '',
  templateItems = [],
  additonalFilters = {},
  selectedFilters,
  handleSetItemFiltersCb,
  showResponseFilter = false,
  hideWhere = false,
  readOnly = false,
}: AdvancedFilterBuilderPropTypes) => {
  const [filterData, setFilterData] = useState<FilterType[]>(
    filters?.length ? filters : [],
  );
  const [itemFilterData, setItemFilterData] = useState<any[]>(
    additonalFilters && additonalFilters?.submissionFilters?.filters
      ? additonalFilters?.submissionFilters?.filters
      : defaultItemFilterState,
  );
  useEffect(() => {
    setItemFilterData(
      additonalFilters && additonalFilters?.submissionFilters?.filters
        ? additonalFilters?.submissionFilters?.filters
        : defaultItemFilterState,
    );
  }, [additonalFilters, templateItems]);

  const handleDeleteRow = (idx: number) => {
    if (
      filterData.length === 1 &&
      filterData[0].filterName &&
      filterData[0].value.length
    ) {
      handleClearFilters?.();
    }
    if (filterData.length === 1) {
      setFilterData(defaultFilterState);
      handleSetFiltersCb(defaultFilterState);
      return;
    }
    const filters = [...filterData];
    filters.splice(idx, 1);

    setFilterData(filters);
    handleSetFiltersCb(filters);
  };

  const setConditionForFilters = (filters: FilterType[]) => {
    const condition =
      filters.length >= 2 ? filters[1].conditional : filters[0].conditional;
    return filters.map((f) => ({ ...f, conditional: condition }));
  };

  const handleSetFilters = (
    idx: number,
    newFilters: {
      filterName?:
        | 'status'
        | 'startDate'
        | 'dueDate'
        | 'template'
        | 'templateSource'
        | 'assignee'
        | 'category'
        | 'location'
        | 'asset'
        | 'completedAt'
        | 'archived'
        | 'taskTypes'
        | 'isOverDue'
        | 'taskTemplates'
        | 'projects'
        | 'schedules'
        | 'priority'
        | 'createdBy'
        | '';
      comparator?: 'is' | 'isNot';
      value?: any[];
      conditional?: 'and' | 'or';
    },
  ) => {
    const filters = [...filterData];
    filters[idx] = { ...filters[idx], ...newFilters };
    const newFilterState = setConditionForFilters(filters);
    setFilterData(newFilterState);
    handleSetFiltersCb(newFilterState);
  };

  const handleAddFilterWithName = (filterName) => {
    const filters = [
      ...filterData,
      {
        ...defaultFilterState[0],
        filterName: filterName,
        conditional: filterData?.[filterData.length - 1]?.conditional || 'and',
      },
    ];
    setFilterData(filters);
    handleSetFiltersCb(filters);
  };

  return (
    <Wrapper>
      {!hideHeader && (
        <Header
          renderCloseIcon={renderCloseIcon}
          handleClearFilters={() => {
            setFilterData([]);
            handleClearFilters?.();
          }}
          handleClose={handleClose}
          readOnly={readOnly}
        />
      )}
      <QuickFilterCnt>
        <Box className="filterList">
          <FiltersList
            filterData={filterData}
            hideFilters={hideFilters}
            addFilter={handleAddFilterWithName}
            handleSetFilters={handleSetFilters}
            readOnly={readOnly}
          />
        </Box>
        <Box className="filterRow">
          <Box className="ScrollableFilterRows">
            {filterData.length === 0 ? (
              <EmptyFilters />
            ) : (
              filterData.map((f, idx) => (
                <FilterRow
                  hideWhere={hideWhere}
                  handleDeleteRow={handleDeleteRow}
                  key={idx}
                  index={idx}
                  handleSetFilters={handleSetFilters}
                  filter={filterData[idx]}
                  hideFilters={hideFilters}
                  dropdownWidth={{
                    mainDropdownWidth: '100%',
                  }}
                  readOnly={readOnly}
                />
              ))
            )}
          </Box>
          {!readOnly && (
            <CustomButton
              variant="outlined"
              color="primary"
              sx={{ width: '100%' }}
              startIcon={<AddIcon />}
              onClick={() => {
                handleSetFiltersCb([...filterData], false, false);
              }}
            >
              Add advanced filter
            </CustomButton>
          )}
        </Box>
      </QuickFilterCnt>
    </Wrapper>
  );
};

const arePropsEqual = (
  prevProps: AdvancedFilterBuilderPropTypes,
  nextProps: AdvancedFilterBuilderPropTypes,
) =>
  isEqual(
    {
      filters: prevProps.filters,
      templateItems: prevProps.templateItems,
      additonalFilters: prevProps.additonalFilters,
    },
    {
      filters: nextProps.filters,
      templateItems: nextProps.templateItems,
      additonalFilters: nextProps.additonalFilters,
    },
  );

export default React.memo(QuickFilterBuilder, arePropsEqual);
